import { TileCacheService } from '@shared/services/basemap/tile-cache.service';

declare const L: any;

/**
 * A Leaflet tile layer that loads tiles with fetch() instead of a bare <img>,
 * so it can attach the JWT and keep what it downloads in IndexedDB.
 *
 * Both are needed for the proxied satellite layer: /api/maptiles sits behind
 * the auth middleware, and an <img> tag cannot send an Authorization header.
 * A plain <img> layer (OpenStreetMap) does not need any of this and should
 * keep using L.tileLayer.
 *
 * Leaflet is loaded as a global script rather than imported, so the subclass is
 * declared at runtime — same approach as L.CustomRectangleMarker elsewhere.
 */

export interface CachedTileLayerOptions {
  tileCache: TileCacheService;

  /** Supplies the bearer token for each request; re-read so refreshes apply. */
  getAuthToken?: () => string | null;

  [option: string]: any;
}

export function createCachedTileLayer(urlTemplate: string, options: CachedTileLayerOptions): any {
  defineCachedTileLayer();
  return new L.CachedTileLayer(urlTemplate, options);
}

function defineCachedTileLayer(): void {
  if (L.CachedTileLayer) return;

  L.CachedTileLayer = L.TileLayer.extend({

    createTile(coords: any, done: (error: any, tile: HTMLImageElement) => void): HTMLImageElement {
      const tile = document.createElement('img');

      tile.alt = '';
      tile.setAttribute('role', 'presentation');

      L.DomEvent.on(tile, 'load', L.Util.bind(this._tileOnLoad, this, done, tile));
      L.DomEvent.on(tile, 'error', L.Util.bind(this._tileOnError, this, done, tile));

      this._loadTile(tile, this.getTileUrl(coords), done);

      return tile;
    },

    /**
     * Cache first, network second. Either way the tile ends up as an object URL
     * so the same code path renders both.
     */
    _loadTile(
      tile: HTMLImageElement,
      url: string,
      done: (error: any, tile: HTMLImageElement) => void
    ): void {
      const cache: TileCacheService | undefined = this.options.tileCache;

      // Resolve through Promise.resolve so a missing or stubbed cache degrades
      // to a network load instead of throwing inside Leaflet's tile pipeline.
      Promise.resolve(cache ? cache.get(url) : null)
        .then((cached: Blob | null) => cached ?? this._fetchTile(url))
        .then((blob: Blob | null) => {
          // The layer may have been removed while the request was in flight.
          if (!this._map) return;

          if (blob) {
            this._renderBlob(tile, blob);
          } else {
            done(new Error('Tile could not be loaded: ' + url), tile);
          }
        })
        .catch((error: unknown) => {
          if (this._map) done(error, tile);
        });
    },

    _fetchTile(url: string): Promise<Blob | null> {
      const headers: Record<string, string> = {};
      const token = this.options.getAuthToken?.();

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      return fetch(url, { headers, credentials: 'same-origin' })
        .then(response => (response.ok ? response.blob() : null))
        .then(blob => {
          // Error bodies must never reach the cache — the proxy answers 503
          // with JSON when upstream is down, and a cached "tile" of JSON would
          // leave a permanent hole in the map.
          if (!blob || !blob.type.startsWith('image/')) return null;

          void this.options.tileCache?.put(url, blob);

          return blob;
        })
        .catch(() => null);
    },

    _renderBlob(tile: HTMLImageElement, blob: Blob): void {
      const objectUrl = URL.createObjectURL(blob);

      // Safe to release once the image has decoded; the tile stays rendered.
      const release = () => URL.revokeObjectURL(objectUrl);
      tile.addEventListener('load', release, { once: true });
      tile.addEventListener('error', release, { once: true });

      tile.src = objectUrl;
    },
  });
}
